import { API_BASE_URL } from "@/api/helpers";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface ToggleGratisParams {
  bookingId: string;
  gratis: boolean;
  /** "YYYY-MM-DD" — set to waive a SINGLE occurrence of a recurring booking; omit for the series. */
  occurrenceDate?: string;
  /** Mandatory when waiving a single occurrence — stored on the waive record for the audit trail. */
  reason?: string;
}

export function useToggleGratis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, gratis, occurrenceDate, reason }: ToggleGratisParams) => {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/gratis`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ gratis, occurrenceDate, reason }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || "Failed to toggle gratis status");
      }

      return response.json();
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ["booking", variables.bookingId] });

      // Snapshot the previous value
      const previousBooking = queryClient.getQueryData(["booking", variables.bookingId]);

      // Optimistically update to the new value — but ONLY for a whole-series waive. A
      // per-occurrence waive (occurrenceDate set) touches waivedOccurrences, not the series
      // `gratis` flag, so flipping it here would briefly paint the entire series free until
      // the refetch corrected it.
      if (!variables.occurrenceDate) {
        queryClient.setQueryData(["booking", variables.bookingId], (old: any) => {
          if (!old) return old;
          return { ...old, gratis: variables.gratis };
        });
      }

      // Return context with the snapshotted value
      return { previousBooking };
    },
    onSuccess: (data, variables) => {
      // Invalidate booking query to refetch updated data from server. This uses React Query's
      // default prefix match, so it also covers the Past-occurrences feed keyed
      // ["booking", id, "occurrence-history"] — a per-occurrence waive refreshes that tab too.
      queryClient.invalidateQueries({ queryKey: ["booking", variables.bookingId] });

      // Show success toast
      toast.success(data.message || "Gratis status updated successfully");
    },
    onError: (error: Error, variables, context) => {
      // Rollback to the previous value on error
      if (context?.previousBooking) {
        queryClient.setQueryData(["booking", variables.bookingId], context.previousBooking);
      }

      // Show error toast
      toast.error(error.message || "Failed to update gratis status");
    },
  });
}
